import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { Fragment, Key } from "react";
import { ISelectOption } from "../types/field.types";

export type IButtonGroupOption<T = string> = ISelectOption<T> &
  Pick<ButtonProps, "startIcon" | "endIcon">;

export interface IButtonGroupProps<T> {
  value: unknown;
  buttonsList: IButtonGroupOption<T>[];
  color?: "primary" | "default";
  onClick: (v: T) => void;
}

export function ButtonGroup<T = string>({
  value,
  buttonsList,
  color = "default",
  onClick,
}: IButtonGroupProps<T>) {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: (theme) => theme.palette.grey[300],
        display: "flex",
        p: 0.5,
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      {buttonsList.map(
        ({ name, value: buttonValue, disabled, ...icons }, index) => {
          const isActive: boolean = value === buttonValue;
          const isActiveNext = value === buttonsList[index - 1]?.value;

          const showDivider = index > 0 && !isActiveNext && !isActive;

          return (
            <Fragment key={buttonValue as Key}>
              {showDivider && (
                <Divider
                  sx={{
                    borderColor: "grey.300",
                    height: (theme) => theme.spacing(5),
                    width: "1px",
                    alignSelf: "center",
                  }}
                  orientation="vertical"
                  flexItem
                />
              )}
              <Button
                {...icons}
                disabled={disabled}
                fullWidth
                variant="contained"
                onClick={() => onClick(buttonValue)}
                color={isActive ? "primary" : "secondary"}
                sx={{
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  ["& .icons:only-child"]: { height: "16px", width: "16px" },
                  lineHeight: 1.25,
                  whiteSpace: "nowrap",
                  minWidth: "unset",
                  color: () => {
                    if (!isActive) {
                      return color === "default" ? "grey.800" : "primary";
                    } else return "primary.contrastText";
                  },
                  padding: (theme) => theme.spacing(2, 4),
                  ["&:hover, &:active"]: {
                    color: () => {
                      if (!isActive) {
                        return "primary.main";
                      } else return "primary.contrastText";
                    },
                  },
                  background: () => {
                    if (!isActive) {
                      return "white";
                    }
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {name}
                </Typography>
              </Button>
            </Fragment>
          );
        },
      )}
    </Box>
  );
}

export default ButtonGroup;
